import { useMyRaceSummary } from '@/features/races/api';
import { formatMoney } from '@/features/races/format';
import type { UserProfile } from '@/features/auth/session';
import { AvatarUploader } from './AvatarUploader';

/**
 * The banner at the top of the profile: who you are, and what you have done.
 *
 * The four figures beneath are the ones this site can actually answer. The
 * design also asked for a GeeCoin balance, a points total and a global rank —
 * all of which belong to a rewards module that does not exist. Rather than print
 * plausible numbers, the strip shows what races knows and the balance gets its
 * own honest card.
 */
export function ProfileHero({ profile }: { profile: UserProfile }) {
  const summary = useMyRaceSummary(true);

  return (
    <section className="profile-panel overflow-hidden rounded-[var(--radius-card)]">
      {/* The decorative wash. Purely presentational, so it is hidden from the
          accessibility tree rather than described. */}
      <div
        aria-hidden
        className="relative h-px w-full"
        style={{
          background: 'linear-gradient(90deg, transparent, #f5c54255 40%, #f5c54288, transparent)',
        }}
      />

      <div className="relative">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-60"
          style={{
            background:
              'radial-gradient(120% 90% at 85% 0%, rgba(245,197,66,0.16), transparent 60%)',
          }}
        />

        <div className="relative flex flex-col gap-5 p-5 sm:flex-row sm:items-center sm:gap-6 sm:p-8">
          <AvatarUploader avatarUrl={profile.avatarUrl} userName={profile.userName} />

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="truncate text-3xl font-semibold tracking-tight text-surface-50">
                {profile.userName}
              </h1>

              {profile.isEmailVerified ? (
                <span title="E-posta adresi doğrulandı" className="text-brand-400">
                  <svg aria-hidden viewBox="0 0 24 24" className="size-5 fill-current">
                    <path d="M12 2 14.4 4.2 17.6 3.7 18.6 6.8 21.6 8.1 20.9 11.3 22.9 13.8 20.9 16.3 21.6 19.5 18.6 20.8 17.6 23.9 14.4 23.4 12 25.6 9.6 23.4 6.4 23.9 5.4 20.8 2.4 19.5 3.1 16.3 1.1 13.8 3.1 11.3 2.4 8.1 5.4 6.8 6.4 3.7 9.6 4.2z" />
                    <path
                      d="m8.5 13.4 2.4 2.4 4.6-4.9"
                      className="fill-none stroke-surface-950"
                      strokeWidth={2.2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span className="sr-only">Doğrulanmış hesap</span>
                </span>
              ) : null}
            </div>

            <p className="mt-1 text-sm text-surface-400">
              Üyelik başlangıcı:{' '}
              {new Intl.DateTimeFormat('tr-TR', { month: 'long', year: 'numeric' }).format(
                new Date(profile.memberSinceUtc),
              )}
            </p>

            {summary.data && summary.data.activeRaces > 0 ? (
              <p className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 px-3 py-1 text-xs font-medium text-emerald-300">
                <span aria-hidden className="size-1.5 rounded-full bg-emerald-400" />
                {summary.data.activeRaces} yarışmada aktifsin
              </p>
            ) : null}

            {summary.data && summary.data.excludedRaces > 0 ? (
              <p className="mt-2 text-xs text-surface-500">
                {summary.data.excludedRaces} yarışma farklı para biriminde ve aşağıdaki toplamlara
                dahil değil.
              </p>
            ) : null}
          </div>
        </div>

        <dl className="relative grid grid-cols-2 gap-px border-t border-surface-800 bg-surface-800 sm:grid-cols-4">
          <Stat
            label="Toplam çevrim"
            value={money(summary.data?.totalWagered, summary.data?.currency)}
            loading={summary.isLoading}
          />
          <Stat
            label="Katıldığın yarışma"
            value={summary.data ? String(summary.data.racesEntered) : null}
            loading={summary.isLoading}
          />
          <Stat
            label="En iyi sıra"
            value={summary.data?.bestRank ? `#${summary.data.bestRank}` : null}
            loading={summary.isLoading}
          />
          <Stat
            label="Kazanılan ödül"
            value={money(summary.data?.prizesWon, summary.data?.currency)}
            loading={summary.isLoading}
          />
        </dl>
      </div>
    </section>
  );
}

/**
 * Totals come back in ONE currency — whichever the member races in most.
 *
 * Null until there is a race to total, and an em dash on screen rather than a
 * zero: "0" claims a measurement was taken and came back empty, which is a
 * different thing from never having entered a race.
 */
function money(amount: number | undefined, currency: string | null | undefined): string | null {
  if (amount === undefined || !currency) {
    return null;
  }

  return formatMoney(amount, currency);
}

function Stat({
  label,
  value,
  loading,
}: {
  label: string;
  value: string | null;
  loading: boolean;
}) {
  return (
    <div className="bg-surface-900/70 px-4 py-5">
      <dt className="text-xs font-medium uppercase tracking-widest text-surface-500">{label}</dt>
      <dd className="mt-1 truncate text-lg font-semibold text-surface-50">
        {loading ? (
          <span aria-hidden className="block h-6 w-20 animate-pulse rounded bg-surface-800" />
        ) : (
          // An em dash, not a zero: "0" claims a measurement was taken and came
          // back empty, which is a different thing from never having raced.
          (value ?? '—')
        )}
      </dd>
    </div>
  );
}
